import { useState, useEffect, useCallback, useMemo } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import RefreshIcon from '@mui/icons-material/Refresh'
import TuneIcon from '@mui/icons-material/Tune'
import api from 'services/api'
import StackedDayChart from '../StreamIngest/StackedDayChart'
import AllowanceChart from './AllowanceChart'
import Lab from './Lab'
import PoolChart from './PoolChart'
import SpendControls from './SpendControls'
import {
  Figure,
  Legend,
  SectionHeading,
  Tile,
  tableBox,
  tableHead,
} from './parts'
import {
  CONTROL_WORDS,
  GATES,
  POOL_COLORS,
  PROVIDER_WORDS,
  TASKS,
  dayLabel,
  fmtAgo,
  fmtCredits,
  fmtPool,
  fmtHrs,
  fmtPct,
  fmtRate,
  fmtRateFrom,
  fmtUnits,
  fmtUsd,
  fmtUsdAxis,
  fmtWhen,
  latestWhole,
  latestWholeWeek,
  poolUsd,
  sumOf,
} from './vocabulary'

// The server shares one response across callers for 60s.
const POLL_INTERVAL_MS = 60_000
const WINDOWS = [14, 30, 90]

const SERIES = TASKS.map(({ key, label, color }) => ({
  key,
  label,
  color,
  valueOf: (row) => row.usd[key] || 0,
}))

const GATE_WORDS = {
  allowance_mode: 'Allowance',
  credit_mode: 'bucket',
  unreviewed_mode: 'unreviewed',
}

const BILL_LINES = {
  vertex: {
    label: 'Vertex AI · stream requests',
    note: 'the selector and the judge',
  },
  cloud_run: {
    label: 'Cloud Run · the two stream services',
    note: 'the meter is router CPU only',
  },
  cloud_storage: {
    label: 'Cloud Storage · stream buckets',
    note: 'not metered',
  },
}

const section = { p: { mobile: 2, desktop: 3 }, borderRadius: 2, minWidth: 0 }
const twoUp = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 440px), 1fr))',
  gap: 3,
  mb: 3,
}

const per = (usd, units) => (units ? fmtUsd(usd / units) : '—')

// A pool figure in a table cell: its dollars, and after them, muted, the
// credits ElevenLabs counts it in. Credits alone from an API with no rate.
const PoolCell = ({ credits, cycle, sx }) => {
  const usd = poolUsd(credits, cycle)
  return (
    <TableCell align="right" sx={sx}>
      {usd == null ? (
        fmtCredits(credits)
      ) : (
        <>
          {fmtUsd(usd)}
          <Box component="span" sx={{ color: 'text.secondary', ml: 1 }}>
            {fmtCredits(credits)}
          </Box>
        </>
      )}
    </TableCell>
  )
}

// How the governor came to today's allowance, in dollars (`cost.py`
// `allowance_basis`): what the whole account had left of the pool, over the
// days to the reset. Before a reconciliation the pool's use is this
// environment's meter alone. Null without the working or the rate.
const allowanceWorking = (today, cycle, ceiling) => {
  const basis = today?.allowance_basis
  if (!basis || poolUsd(0, cycle) == null) return null
  const usd = (credits) => fmtUsd(poolUsd(credits, cycle))
  const days = `${basis.days_left} day${basis.days_left === 1 ? '' : 's'} left`
  const overage = basis.overage_left_credits
    ? usd(basis.overage_left_credits)
    : null
  const share =
    basis.remaining_credits > 0
      ? `(${usd(basis.credits_limit)} pool − ${usd(
          basis.credits_used_reported + basis.tail_credits
        )} ${basis.reconciled ? 'used' : 'metered here'}${
          overage ? ` + ${overage} overage` : ''
        }) ÷ ${days}`
      : `The ${usd(basis.credits_limit)} pool is used up${
          overage ? `, ${overage} of overage left` : ''
        }, ${days}`
  const capped =
    ceiling != null && ceiling < today.allowance_hours
      ? `, under the ${fmtHrs(ceiling)} daily ceiling`
      : ''
  return `${share}${capped}`
}

// A unit cost over the window's whole days: small bars from zero, the newest
// darker, and the range beside the title.
const Trend = ({ title, rows, valueOf }) => {
  const points = rows
    .map((row) => ({ row, value: valueOf(row) }))
    .filter(({ value }) => Number.isFinite(value))
  if (points.length < 2) return null
  const values = points.map(({ value }) => value)
  const high = Math.max(...values)
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="body2" fontWeight={500}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {fmtUsd(Math.min(...values))} to {fmtUsd(high)}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '3px',
          height: 48,
          mt: 0.75,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {points.map(({ row, value }, index) => (
          <Tooltip
            key={row.day}
            title={`${dayLabel(row.day)}: ${fmtUsd(value)}`}
          >
            <Box
              sx={{
                flex: '1 1 0',
                minWidth: 0,
                height: `${(value / high) * 44}px`,
                bgcolor: TASKS[0].color,
                borderRadius: '3px 3px 0 0',
                opacity: index === points.length - 1 ? 1 : 0.55,
              }}
            />
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {dayLabel(points[0].row.day)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {dayLabel(points[points.length - 1].row.day)}
        </Typography>
      </Box>
    </Box>
  )
}

const StreamCost = () => {
  const [days, setDays] = useState(14)
  const [cost, setCost] = useState(null)
  const [controls, setControls] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedDay, setSelectedDay] = useState(null)
  const [lab, setLab] = useState(null)
  const [labError, setLabError] = useState(null)

  // The cost lab's read, apart from the spend's: it fails or lags on its own.
  const loadLab = useCallback(async () => {
    try {
      setLab(await api.streamMetrics.getLab())
      setLabError(null)
    } catch (err) {
      setLabError(err.message)
    }
  }, [])

  useEffect(() => {
    loadLab()
    const timer = setInterval(loadLab, POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [loadLab])

  const load = useCallback(async () => {
    try {
      const [next, gates] = await Promise.all([
        api.streamMetrics.getCost({ days }),
        api.streamControls.getControls(),
      ])
      setCost(next)
      setControls(gates)
      setError(null)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => {
    setLoading(true)
    load()
    const timer = setInterval(load, POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [load])

  const daily = useMemo(() => cost?.daily || [], [cost])
  const newestFirst = useMemo(() => [...daily].reverse(), [daily])
  const wholeDays = useMemo(
    () => daily.filter(({ books }) => books === 'whole'),
    [daily]
  )
  const cycle = cost?.cycle
  const bill = cost?.billing
  const gates = controls?.controls || {}
  const enforcing = GATES.filter((name) => gates[name]?.value === 'enforcing')

  const labSentence = lab?.ready
    ? Object.values(lab.headlines || {})[0]?.sentences?.[0]
    : null

  const whole = latestWhole(daily)
  const week = latestWholeWeek(daily)
  const today = daily.find(({ books }) => books === 'running')
  const ceiling = gates.daily_ceiling_hours?.value ?? null
  const poolShare = today?.allowance_hours ?? null
  const allowed =
    poolShare != null && ceiling != null
      ? Math.min(poolShare, ceiling)
      : poolShare
  const limit = cycle?.credits_limit
  // The cycle's reported total, which every reconciliation writes; the
  // per-day series is only for the chart, and an older cycle row has none.
  const used = cycle?.reported_through
    ? cycle.credits_used_reported ?? cycle.our_credits
    : cycle?.our_credits
  // The pool in dollars once the API sends the contract's rate.
  const priced = poolUsd(0, cycle) != null
  const allowedCredits =
    today?.allowance_credits != null && ceiling != null && priced
      ? Math.min(
          today.allowance_credits,
          ceiling * cycle.credits_per_audio_hour
        )
      : today?.allowance_credits
  const allowedUsd = poolUsd(allowedCredits, cycle)
  const working = allowanceWorking(today, cycle, ceiling)
  const paygo = cycle
    ? cycle.spend_usd.find_moments +
      cycle.spend_usd.judge +
      cycle.spend_usd.route
    : null
  const yesterday = cost
    ? new Date(new Date(`${cost.to}T00:00:00Z`).getTime() - 86400000)
        .toISOString()
        .slice(0, 10)
    : null
  const shown =
    daily.find(({ day }) => day === selectedDay) ||
    whole ||
    daily[daily.length - 1]
  const reported = [...daily]
    .filter(({ reported_credits }) => reported_credits != null)
    .reverse()
    .slice(0, 7)
  const totalUsd = sumOf(cost?.by_task || [], (row) => row.usd)
  const skipped = (gate) => sumOf(daily, (row) => row.would_skip_hours[gate])

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Spend per UTC day off the fleet's daily rows
          {cost ? ` · ${dayLabel(cost.from)} to ${dayLabel(cost.to)}` : ''}
          {' · cached 60s'}
        </Typography>
        {loading && <CircularProgress size={18} />}
        {/* On a phone the window and refresh share a line, and the controls
            button takes the next one whole. */}
        <Box sx={{ flex: 1, display: { mobile: 'none', desktop: 'block' } }} />
        <ToggleButtonGroup
          value={days}
          exclusive
          size="small"
          onChange={(event, next) => next && setDays(next)}
        >
          {WINDOWS.map((value) => (
            <ToggleButton key={value} value={value}>
              {value}d
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Button
          variant="contained"
          disableElevation
          startIcon={<TuneIcon />}
          onClick={() =>
            document
              .getElementById('spend-controls')
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
          sx={{
            textTransform: 'none',
            order: { mobile: 1, desktop: 0 },
            width: { mobile: '100%', desktop: 'auto' },
          }}
        >
          Spend controls
          <Box
            component="span"
            sx={{
              ml: 1,
              px: 0.75,
              borderRadius: 2.5,
              fontSize: 12,
              fontWeight: 700,
              bgcolor: 'primary.contrastText',
              color: 'primary.main',
            }}
          >
            {enforcing.length} of 3
          </Box>
        </Button>
        <Tooltip title="Refresh">
          <IconButton
            size="small"
            onClick={() => {
              load()
              loadLab()
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}
      {enforcing.length > 0 && (
        <Alert severity="warning" sx={{ my: 2 }}>
          <b>
            Enforcing:{' '}
            {enforcing
              .map((name) => CONTROL_WORDS[name].toLowerCase())
              .join(' and ')}
            .
          </b>{' '}
          A run a gate turns aside is recorded as skipped and is never
          transcribed later.
        </Alert>
      )}
      {cycle?.over_pool && (
        <Alert severity="error" sx={{ my: 2 }}>
          The ElevenLabs account is past its included credits this cycle.
        </Alert>
      )}

      {/* The cost lab's window in a sentence: what streamed, what was sent,
          what it cut and what it used of the pool. */}
      {labSentence && (
        <Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
          <Typography variant="body1">{labSentence}</Typography>
          <Button
            size="small"
            sx={{ mt: 0.5, ml: -0.5, textTransform: 'none' }}
            onClick={() =>
              document
                .getElementById('lab')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          >
            What would a change save?
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, my: 3 }}>
        <Tile
          label="ElevenLabs pool this cycle"
          value={limit ? fmtPct(used / limit) : '—'}
          note={
            cycle
              ? `${
                  priced
                    ? `${fmtUsd(poolUsd(used, cycle))} of the ${fmtUsd(
                        poolUsd(limit, cycle)
                      )} prepaid`
                    : `${fmtCredits(used)} of ${fmtCredits(limit)} credits`
                }${
                  cycle.reported_through
                    ? `, through ${dayLabel(cycle.reported_through)}`
                    : ", this environment's meter"
                }`
              : null
          }
          sub={
            cycle
              ? `${
                  priced
                    ? `${fmtCredits(used)} of ${fmtCredits(limit)} credits · `
                    : ''
                }Resets ${dayLabel(cycle.reset)}, in ${
                  cycle.days_left
                } days · ${fmtPct(cycle.projected_credits / limit)} projected`
              : null
          }
          help="As ElevenLabs reports it, through its last complete day: every environment and manual clips. Projected at the last 7 complete days' pace."
        />
        <Tile
          label="Today's allowance"
          value={
            allowedUsd == null
              ? fmtHrs(allowed)
              : `${fmtUsd(allowedUsd)} · ${fmtHrs(allowed)}`
          }
          note={
            allowed == null
              ? 'no run yet today'
              : `${fmtHrs(today?.transcribed_hours)} used so far (${fmtPct(
                  (today?.transcribed_hours || 0) / allowed
                )})`
          }
          sub={
            working
              ? `${working}, fixed at the day's first run`
              : cycle?.reported_through
              ? "Fixed at the day's first run, from the reconciled pool"
              : "Fixed at the day's first run, from this environment's estimate"
          }
          help={`The day's share of the ${
            priced ? `${fmtUsd(poolUsd(limit, cycle))} prepaid ` : ''
          }pool: what the whole account has left of it this cycle over the days to the reset, fixed at the day's first run, and under the daily ceiling when one is set.`}
        />
        <Tile
          label={
            !whole
              ? 'Spent yesterday'
              : whole.day === yesterday
              ? 'Spent yesterday'
              : `Spent ${dayLabel(whole.day)}`
          }
          value={fmtUsd(whole?.usd_total)}
          note={
            whole
              ? `${fmtUsd(whole.usd.transcribe)} of it inside the prepaid pool`
              : null
          }
          sub={
            whole
              ? `${fmtUsd(
                  whole.usd_total - whole.usd.transcribe
                )} pay-as-you-go, on ${dayLabel(whole.day)}`
              : null
          }
          help="The newest whole day: every metered provider call, at the price list's rates."
        />
        <Tile
          label="Spend gates"
          value={
            !enforcing.length
              ? 'Shadow'
              : enforcing.length === GATES.length
              ? 'Enforcing'
              : `${enforcing.length} of ${GATES.length} enforcing`
          }
          note={GATES.map(
            (name) => `${GATE_WORDS[name]} ${gates[name]?.value || '—'}`
          ).join(' · ')}
          sub={
            cycle?.reported_at
              ? `Reconciled with ElevenLabs ${fmtAgo(cycle.reported_at)}`
              : 'Not reconciled with ElevenLabs yet'
          }
          help="The daily allowance, the per-user credit bucket and the unreviewed-quotes gate. Change them under Spend controls."
        />
      </Box>

      <Paper variant="outlined" sx={{ ...section, mb: 3 }}>
        <SectionHeading
          title="What a quote costs"
          subtitle="All spend over whole days. Today is still filling and is left out."
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <Figure
            value={per(whole?.usd_total, whole?.quotes)}
            label="per quote"
            note={
              whole ? `${whole.quotes} quotes on ${dayLabel(whole.day)}` : null
            }
          />
          <Figure
            value={per(whole?.usd_total, whole?.transcribed_hours)}
            label="per audio hour transcribed"
            note="all spend; ElevenLabs is $0.15 of it"
          />
          <Figure
            value={per(whole?.usd_total, whole?.captured_hours)}
            label="per captured hour"
            note={
              whole?.captured_hours == null
                ? 'the router meter starts Oct 21'
                : `${fmtHrs(whole.captured_hours)} recorded on ${dayLabel(
                    whole.day
                  )}`
            }
          />
          <Figure
            value={fmtUsd(whole?.usd_total)}
            label={whole ? `a day · ${dayLabel(whole.day)}` : 'a day'}
            note={today ? `today so far ${fmtUsd(today.usd_total)}` : null}
          />
          <Figure
            value={week ? fmtUsd(sumOf(week, (row) => row.usd_total)) : '—'}
            label={
              week
                ? `a week · ${dayLabel(week[0].day)} to ${dayLabel(
                    week[6].day
                  )}`
                : 'a week'
            }
            note={
              week
                ? `${fmtUsd(sumOf(week, (row) => row.usd_total) / 7)} a day`
                : 'no whole Monday-to-Sunday week in this window yet'
            }
          />
          <Figure
            value={fmtUsd(paygo)}
            label="pay-as-you-go this cycle"
            note="Gemini and Cloud Run; ElevenLabs is prepaid"
          />
        </Box>
        <Box sx={{ ...twoUp, mt: 2.5, mb: 0 }}>
          <Trend
            title="Per quote, by day"
            rows={wholeDays}
            valueOf={(row) => (row.quotes ? row.usd_total / row.quotes : NaN)}
          />
          <Trend
            title="Per audio hour transcribed, by day"
            rows={wholeDays}
            valueOf={(row) =>
              row.transcribed_hours
                ? row.usd_total / row.transcribed_hours
                : NaN
            }
          />
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ ...section, mb: 3 }}>
        <SectionHeading
          title="Spend by day"
          subtitle="Every metered provider call, by the task that made it, on the UTC day it was spent. Transcription is priced at the contract rate, inside the prepaid pool."
        />
        <Legend items={TASKS} />
        <StackedDayChart
          days={newestFirst}
          series={SERIES}
          height={240}
          selectedDay={selectedDay}
          onSelectDay={setSelectedDay}
          fadedDay={today?.day}
          formatValue={fmtUsd}
          formatAxis={fmtUsdAxis}
          minInterval={0}
          emptyText="Nothing metered in this window."
        />
        {shown && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <Box
              component="span"
              sx={{ color: 'text.primary', fontWeight: 700 }}
            >
              {dayLabel(shown.day)}
              {shown.books === 'running' ? ' so far' : ''} ·{' '}
              {fmtUsd(shown.usd_total)}
            </Box>
            {' · '}
            {TASKS.map(
              ({ key, label }) =>
                `${label.split(' · ')[0].toLowerCase()} ${fmtUsd(
                  shown.usd[key]
                )}`
            ).join(' · ')}
            {` · ${shown.quotes} quotes`}
          </Typography>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ mt: 1 }}
        >
          The faded column is today, still filling. Click a day for its
          breakdown; route starts with the router meter on Oct 21, and a day
          before it has none rather than $0.
        </Typography>
      </Paper>

      <Box sx={twoUp}>
        <Paper variant="outlined" sx={section}>
          <SectionHeading
            title="Transcription against the daily allowance"
            subtitle="Each day's transcribed hours as a share of that day's allowance. A bar past the line is what enforcing would skip."
          />
          <AllowanceChart daily={daily} />
          <Typography variant="caption" color="text.secondary" component="div">
            In shadow every bar is sent; the allowance would have skipped{' '}
            {fmtHrs(skipped('allowance'))} here. Two separate gates would have
            skipped more: the credit bucket {fmtHrs(skipped('credits'))} and the
            unreviewed gate {fmtHrs(skipped('unreviewed'))}.
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={section}>
          <SectionHeading
            title="ElevenLabs pool this cycle"
            subtitle="What the account has used of the prepaid pool so far, as ElevenLabs reports it, split into this environment's streams and everything else on the account."
          />
          <Legend
            items={[
              {
                key: 'ours',
                label: "This environment's streams",
                color: POOL_COLORS.ours,
              },
              {
                key: 'theirs',
                label: 'Manual clips and other environments',
                color: POOL_COLORS.theirs,
              },
              {
                key: 'pace',
                label: 'Even pace',
                color: '#9e9e9e',
                dashed: true,
              },
              {
                key: 'ahead',
                label: 'Projected, whole account',
                color: '#9e9e9e',
                outline: true,
              },
            ]}
          />
          <PoolChart cycle={cycle} />
          {cycle && (
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
            >
              At the recent pace the cycle ends at{' '}
              {fmtPool(cycle.projected_credits, cycle)},{' '}
              {fmtPct(cycle.projected_credits / limit)} of the{' '}
              {priced ? 'prepaid pool' : 'included credits'}. Credits left at
              the reset do not carry over.
            </Typography>
          )}
        </Paper>
      </Box>

      <Box sx={twoUp}>
        <Paper variant="outlined" sx={section}>
          <SectionHeading
            title="ElevenLabs, as ElevenLabs reports it"
            subtitle="Read from the usage and subscription APIs every 6 hours. The account is shared, so these figures include manual clips and every environment."
          />
          {cycle && (
            <Box
              component="dl"
              sx={{
                m: 0,
                mb: 2,
                display: 'grid',
                // On a phone each figure sits under its label.
                gridTemplateColumns: {
                  mobile: '1fr',
                  desktop: 'minmax(150px, auto) 1fr',
                },
                columnGap: 3,
                rowGap: { mobile: 0, desktop: 0.75 },
                typography: 'body2',
                '& dt': { color: 'text.secondary' },
                '& dd': { m: 0, mb: { mobile: 1, desktop: 0 } },
              }}
            >
              <dt>Included</dt>
              <dd>
                {priced
                  ? `${fmtUsd(
                      cycle.flat_fee_usd
                    )} flat a cycle, for ${fmtCredits(limit)} credits at $${
                      cycle.credit_usd
                    } each`
                  : `${fmtCredits(limit)} credits a cycle, ${fmtUsd(
                      cycle.flat_fee_usd
                    )} flat`}
              </dd>
              <dt>
                {cycle.reported_through
                  ? `Used through ${dayLabel(cycle.reported_through)}`
                  : 'Used'}
              </dt>
              <dd>
                {!cycle.reported_through
                  ? 'not reconciled yet'
                  : priced
                  ? `${fmtUsd(poolUsd(used, cycle))} of ${fmtUsd(
                      poolUsd(limit, cycle)
                    )} (${fmtPct(used / limit)}) · ${fmtCredits(used)} credits`
                  : `${fmtCredits(used)} of ${fmtCredits(
                      limit
                    )} credits (${fmtPct(used / limit)})`}
              </dd>
              <dt>By model</dt>
              <dd>
                {Object.entries(cycle.by_product_model || {})
                  .map(([key, credits]) => {
                    const usd = poolUsd(credits, cycle)
                    return `${key.split('/').pop()} ${
                      usd == null
                        ? fmtCredits(credits)
                        : `${fmtUsd(usd)} (${fmtCredits(credits)})`
                    }`
                  })
                  .join(' · ') || '—'}
              </dd>
              <dt>Metered here</dt>
              <dd>
                {fmtPool(cycle.our_credits, cycle)} by this environment's
                streams
                {used
                  ? `, ${fmtPct(cycle.our_credits / used)} of the account`
                  : ''}
              </dd>
              <dt>Overage</dt>
              <dd>
                {Number(cycle.current_overage)
                  ? fmtUsd(Number(cycle.current_overage))
                  : 'None this cycle'}
                {cycle.overage_credit_usd != null &&
                  ` · past the pool $${cycle.overage_credit_usd} a credit`}{' '}
                · can extend: {cycle.can_extend_character_limit ? 'yes' : 'no'}
              </dd>
              <dt>Last read</dt>
              <dd>
                {cycle.reported_at ? fmtWhen(cycle.reported_at) : 'never'}
                {cycle.reported_through
                  ? ` · complete through ${dayLabel(cycle.reported_through)}`
                  : ''}
              </dd>
            </Box>
          )}
          <Box sx={tableBox}>
            <Table size="small" sx={tableHead}>
              <TableHead>
                <TableRow>
                  <TableCell>Day</TableCell>
                  <TableCell align="right">ElevenLabs reported</TableCell>
                  <TableCell align="right">
                    This environment's streams
                  </TableCell>
                  <TableCell align="right">Everything else</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reported.length ? (
                  reported.map((row) => (
                    <TableRow key={row.day}>
                      <TableCell>{dayLabel(row.day)}</TableCell>
                      <PoolCell credits={row.reported_credits} cycle={cycle} />
                      <PoolCell credits={row.our_credits} cycle={cycle} />
                      <PoolCell
                        credits={Math.max(
                          0,
                          row.reported_credits - row.our_credits
                        )}
                        cycle={cycle}
                        sx={{ color: 'text.secondary' }}
                      />
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ color: 'text.secondary' }}>
                      No complete day reported in this window yet. The 6-hourly
                      reconciliation fills these in.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={section}>
          <SectionHeading
            title="GCP billing export"
            subtitle={`What Google billed, about a day behind, beside what this page metered.${
              bill?.days
                ? ` The ${
                    bill.days === 7 ? 'last 7 days' : `${bill.days} days`
                  } the bill holds, ${dayLabel(bill.from)} to ${dayLabel(
                    bill.to
                  )}.`
                : ''
            }`}
          />
          <Box sx={tableBox}>
            <Table size="small" sx={tableHead}>
              <TableHead>
                <TableRow>
                  <TableCell>Line</TableCell>
                  <TableCell align="right">Billed</TableCell>
                  <TableCell align="right">Metered</TableCell>
                  <TableCell>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bill?.days ? (
                  <>
                    {bill.lines.map((line) => (
                      <TableRow key={line.line}>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {BILL_LINES[line.line].label}
                        </TableCell>
                        <TableCell align="right">
                          {fmtUsd(line.stream)}
                        </TableCell>
                        <TableCell align="right">
                          {line.metered == null ? '—' : fmtUsd(line.metered)}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {BILL_LINES[line.line].note} · whole project{' '}
                          {fmtUsd(line.project)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>
                        ElevenLabs · Marketplace
                      </TableCell>
                      <TableCell align="right">
                        {fmtUsd(cycle?.elevenlabs_billed_usd)}
                      </TableCell>
                      <TableCell align="right">—</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        this cycle, account-wide
                      </TableCell>
                    </TableRow>
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ color: 'text.secondary' }}>
                      Not read yet. The 6-hourly reconciliation reads the GCP
                      billing export, about a day behind; a day nothing streamed
                      has nothing to compare.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ mt: 1.5 }}
          >
            The stream lane's Gemini requests, its two Cloud Run services and
            its buckets carry the billing label feature=audio-streaming; days
            before the label show only the whole project. ElevenLabs is billed
            through Google Cloud Marketplace: the flat fee each month on the
            24th, and any overage the month after.
          </Typography>
        </Paper>
      </Box>

      <Paper variant="outlined" sx={{ ...section, mb: 3 }}>
        <SectionHeading
          title="Per task, per model"
          subtitle={
            cost
              ? `${dayLabel(cost.from)} to ${dayLabel(cost.to)}, today so far.`
              : null
          }
        />
        <Box sx={tableBox}>
          <Table size="small" sx={[tableHead, { minWidth: 860 }]}>
            <TableHead>
              <TableRow>
                <TableCell>Task</TableCell>
                <TableCell>Provider · model</TableCell>
                <TableCell>Units</TableCell>
                <TableCell>Rate</TableCell>
                <TableCell>Rate from</TableCell>
                <TableCell align="right">Spend</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(cost?.by_task || []).map((row) => {
                const task = TASKS.find(({ key }) => key === row.task)
                return (
                  <TableRow key={row.task}>
                    <TableCell>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '3px',
                            bgcolor: task?.color,
                          }}
                        />
                        {task?.label.split(' · ')[0]}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {PROVIDER_WORDS[row.provider] || row.provider} ·{' '}
                      {row.model}
                    </TableCell>
                    <TableCell>{fmtUnits(row)}</TableCell>
                    <TableCell>{fmtRate(row)}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {fmtRateFrom(row)}
                    </TableCell>
                    <TableCell align="right">{fmtUsd(row.usd)}</TableCell>
                  </TableRow>
                )
              })}
              <TableRow
                sx={{ '& td': { fontWeight: 700, bgcolor: 'action.hover' } }}
              >
                <TableCell colSpan={5}>Total</TableCell>
                <TableCell align="right">{fmtUsd(totalUsd)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{ ...section, mb: 3, scrollMarginTop: '16px' }}
        id="lab"
      >
        <Lab
          lab={lab}
          error={labError}
          controls={controls}
          onSaved={setControls}
        />
      </Paper>

      <Paper
        variant="outlined"
        sx={{ ...section, mb: 3, scrollMarginTop: '16px' }}
        id="spend-controls"
      >
        <SpendControls view={controls} cost={cost} onSaved={setControls} />
      </Paper>

      <Paper variant="outlined" sx={{ ...section, mb: 3 }}>
        <SectionHeading title="Not on this page" />
        <Typography variant="body2" color="text.secondary">
          MongoDB Atlas, network egress and the admin's own reads are not
          attributed to streaming. One account's usage, day by day, is on the
          Users page.
        </Typography>
      </Paper>
    </Box>
  )
}

export default StreamCost
